import { isGlobalFeatureEnabled, isAccountFeatureEnabled } from 'owa-feature-flags';
import { getConfig } from 'owa-service/lib/config';
import isNestedAppAuthAvailable from 'owa-nested-app-auth/lib/isNestedAppAuthAvailable';
import { IMMERSIVEBIZCHAT_CLIENT_ID } from 'owa-nested-app-auth/lib/constants';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import type { MailboxInfo } from 'owa-client-types';
import { isServiceRequestSupportedForMailbox } from 'owa-service/lib/utils/isServiceRequestSupportedForMailbox';
import { getUnifiedInboxResolvedMailboxInfoOrSelf } from 'owa-session-store';
import getApplicationSettings from 'owa-application-settings/lib/selectors/getApplicationSettings';

// Period of time since the last prefetch after which we will not prefetch again
// TODO: Consider making this dynamic based on token expiry and moving to Prefetcher
// For example, if we last prefetched a token 30 minutes ago, and the token expires in
// 2 hours, we can assume that the token is still valid and cached within BizChat, and
// we should not uselessly prefetch it again (to lower eSTS burden).
const PREFETCH_COOLDOWN_TIME = 60 * 60 * 1000; // 60 minutes

const PREFETCH_SCOPES = [
    'https://substrate.office.com/sydney/.default',
    'https://substrate.office.com/search/.default',
    'https://loki.delve.office.com//.default', // purposeful double slash
    'https://api.spaces.skype.com/.default',
];
const PREFETCH_HOST = 'outlook.office.com'; // TODO: read this from the app definition

let lastPrefetchTime: number = 0;

export async function prefetchBizChatAuthTokens(
    scenarioName: string,
    mailboxInfo?: MailboxInfo
): Promise<void> {
    await Promise.resolve(); // Don't tie up the event loop with this

    let requestMailboxInfo = mailboxInfo ?? getModuleContextMailboxInfo();
    requestMailboxInfo = getUnifiedInboxResolvedMailboxInfoOrSelf(requestMailboxInfo);
    if (!isServiceRequestSupportedForMailbox(requestMailboxInfo)) {
        // If the mailbox type does not support service requests, do not attempt to prefetch
        throw new Error('Cannot prefetch BizChat auth tokens - unsupported mailbox type');
    }

    try {
        if (
            isGlobalFeatureEnabled('mon-copilot-bizchat') &&
            isGlobalFeatureEnabled('auth-naa-prefetch') &&
            isNestedAppAuthAvailable()
        ) {
            if (lastPrefetchTime > Date.now() - PREFETCH_COOLDOWN_TIME) {
                // If we have prefetched recently, do not prefetch again
                return;
            }

            const prefetchNaaRequest = getConfig().prefetchNaaRequest;
            if (prefetchNaaRequest) {
                const hostToUse = isAccountFeatureEnabled(
                    'mon-copilot-bizchat-ocdi',
                    requestMailboxInfo
                )
                    ? getApplicationSettings('BizchatOcdiEndpoint', requestMailboxInfo).hostName
                    : PREFETCH_HOST;
                for (const scope of PREFETCH_SCOPES) {
                    prefetchNaaRequest(
                        requestMailboxInfo,
                        {
                            clientId: IMMERSIVEBIZCHAT_CLIENT_ID,
                            scope,
                            authenticationScheme: 'Bearer',
                        },
                        hostToUse,
                        scenarioName
                    );
                }

                lastPrefetchTime = Date.now();
            }
        }
    } catch {}
}
